import { X } from "lucide-react";
import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { InputField } from "@/components/common";
import { cn } from "@/lib/utils";

const resolveCompanyLabel = (company) =>
  company?.companyName ||
  company?.name ||
  company?.unitName ||
  company?.label ||
  "Unknown Company";

const resolveCompanyId = (company) => company?._id || company?.id;

const SurveyCompanySelector = ({
  companies = [],
  selectedCompanyIds = [],
  companySelectionMode = "all",
  searchValue = "",
  disabled = false,
  onSearchChange,
  onModeChange,
  onToggleCompany,
}) => {
  const { t } = useTran();
  const filteredCompanies = companies.filter((company) =>
    resolveCompanyLabel(company)
      .toLowerCase()
      .includes(searchValue.toLowerCase()),
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("Company Selection")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("Define which companies can respond to this survey.")}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background p-1 text-xs font-semibold">
          {[
            { key: "all", label: t("All companies") },
            { key: "customize", label: t("Customize") },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              className={cn(
                "rounded-full px-3 py-1 transition",
                companySelectionMode === item.key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => onModeChange?.(item.key)}
              disabled={disabled}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {companySelectionMode === "customize" ? (
        <div className="space-y-3">
          <InputField
            placeholder={t("Search companies...")}
            value={searchValue}
            onChange={(event) => onSearchChange?.(event.target.value)}
            disabled={disabled}
          />
          <div className="grid gap-2 sm:grid-cols-2">
            {filteredCompanies.length === 0 ? (
              <div className="rounded-radius border border-dashed border-border bg-muted/40 p-4 text-xs text-muted-foreground">
                {t("No companies found.")}
              </div>
            ) : (
              filteredCompanies.map((company) => {
                const companyId = resolveCompanyId(company);
                const checked = selectedCompanyIds.includes(companyId);
                return (
                  <label
                    key={companyId}
                    className="flex items-center gap-2 rounded-radius border border-border bg-card px-3 py-2 text-xs text-foreground shadow-sm transition hover:border-primary/50"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => onToggleCompany?.(companyId)}
                      disabled={disabled}
                    />
                    <span>{resolveCompanyLabel(company)}</span>
                  </label>
                );
              })
            )}
          </div>
          {selectedCompanyIds.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selectedCompanyIds.map((companyId) => {
                const company = companies.find(
                  (item) => resolveCompanyId(item) === companyId,
                );
                return (
                  <Badge
                    key={companyId}
                    variant="secondary"
                    className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium"
                  >
                    {resolveCompanyLabel(company)}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => onToggleCompany?.(companyId)}
                    />
                  </Badge>
                );
              })}
            </div>
          ) : null}
          <p className="text-[10px] text-muted-foreground">
            {t("Selected")} {selectedCompanyIds.length}{" "}
            {selectedCompanyIds.length === 1 ? t("company") : t("companies")}
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default SurveyCompanySelector;
